import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { deleteInviteFromCache } from '../queries/company-cache';

type UseDeclineInviteActionData = {
    declineInvite: (inviteId: string) => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Turning down a company invite.
 *
 * Carries no `isAllowed` of its own: an invite is addressed to the person
 * looking at it, so there is nobody who can see one they may not answer.
 */
export function useDeclineInviteAction(): UseDeclineInviteActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/invites/{id}/decline` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'INVITE_NOT_FOUND')) {
                return t('shared.errors.invite_not_found');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (inviteId: string) =>
            openApiRef.current.invites.declineInvite(inviteId).then(() => {}),
        onSuccess: (_data, inviteId) => {
            deleteInviteFromCache(queryClient, inviteId);

            UIToast.success(t('actions.decline_invite.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        declineInvite: (inviteId) => mutation.mutateAsync(inviteId),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
